import { FC, useEffect, useState } from 'react';
import { Input, InputGroup } from 'reactstrap';
import { useParsedSearchParams, useSelectedAsset } from '../../../lib/util';
import classes from './CertificateSearch.module.scss';
import { Alert, Button, Card } from '@bphxd/ds-core-react';
import { useQuery } from '@apollo/client';
import { GET_ALL_CERTIFICATES } from '../../../graph-queries/tar/certificate';
import { Search24 } from '@bphxd/ds-core-react/lib/icons';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useSalutation } from '../../../lib/util/account';
import AppLayout from '../../templates/AppLayout';
import { MAINTANENCE_PATH } from '../../../constants';
import { CertificateStatusEnum } from '../../../graph-queries/__generated__/graphql';

const MIN_CERTIFICATE_LENGTH = 3;

const CertificateSearch: FC = () => {
  const navigate = useNavigate();
  const asset = useSelectedAsset();
  const [certificate, setCertificate] = useParsedSearchParams('certificate');
  const [inputCertificate, setInputCertificate] = useState(certificate);
  const { salutation, firstName } = useSalutation();
  const [error, setError] = useState('');
  const {
    data,
    loading,
    error: certificateLoadError,
  } = useQuery(GET_ALL_CERTIFICATES, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });

  const approvedCertificates = (data?.allCertificates || []).filter(
    (cert) => cert.status === CertificateStatusEnum.Approved
  );

  const filteredCertificates = approvedCertificates.filter((cert) =>
    cert.certificateNumber.toLowerCase().includes(certificate.toLowerCase())
  );

  const searchCertificate = (inputCertificate: string) => {
    if (inputCertificate.length > MIN_CERTIFICATE_LENGTH) {
      setCertificate(inputCertificate);
    } else {
      setError(`Invalid search: ${inputCertificate}`);
    }
  };

  useEffect(() => {
    if (certificateLoadError) {
      setError(`Failed to fetch: ${inputCertificate}`);
    } else if (data && !loading) {
      if (filteredCertificates.length > 0) {
        setError('');
        if (certificate !== inputCertificate) setCertificate(inputCertificate);
      } else if (inputCertificate.length > 0) {
        // no error when search string is empty initially
        setError(`Invalid: ${inputCertificate}`);
      }
    }
  }, [data, loading, certificateLoadError]);

  useEffect(() => {
    setError('');
    setInputCertificate(certificate);
  }, [certificate]);

  useEffect(() => {
    setError('');
  }, [inputCertificate]);

  return (
    <AppLayout>
      <section className={clsx('pb-12 pt-10 text-center', classes.search)}>
        <div className={classes.title}>
          {salutation}
          {firstName}
        </div>
        <InputGroup className="w-50 d-inline-flex gap-5">
          <Input
            value={inputCertificate}
            onChange={(e) => setInputCertificate(e.target.value)}
            placeholder="Search by certificate number"
            className="bg-primary"
          />
          <Button
            color="light"
            className={classes.searchBtn}
            onClick={() => {
              const formattedInputCertificate = inputCertificate.toUpperCase();
              searchCertificate(formattedInputCertificate);
            }}
            disabled={loading || !asset?.assetId}
            Icon={Search24}
          >
            {loading ? 'Searching' : 'Search'}
          </Button>

          {error && (
            <Alert
              className="w-100 d-inline-block position-absolute"
              color="danger"
              style={{ top: 48 }}
            >
              {error}
            </Alert>
          )}
        </InputGroup>
      </section>
      <section className={classes.results}>
        {filteredCertificates.map((cert) => {
          return (
            <Card key={cert.certificateId} className={classes.result}>
              <div className={classes.certificateNumber}>
                <div className={classes.header}>Certificate Number</div>
                <div>{cert.certificateNumber}</div>
              </div>
              <div className={classes.title}>
                <div className={classes.header}>Certificate Title</div>
                <div>{cert.title}</div>
              </div>
              <div className={classes.action}>
                <Button
                  size="lg"
                  rounded="0"
                  onClick={() =>
                    navigate(
                      `/${MAINTANENCE_PATH}/certificates/execute/${cert.certificateId}?asset=${asset?.assetName}`,
                      {
                        state: {
                          search: certificate,
                        },
                      }
                    )
                  }
                >
                  Review
                </Button>
              </div>
            </Card>
          );
        })}
      </section>
    </AppLayout>
  );
};

export default CertificateSearch;
